import React, { Suspense, useEffect, useRef, useState } from 'react';
import { getRequests, clearRequests, subscribe, DEBUG_ENABLED } from './requestLog.js';
import { installFetchTap } from './installFetchTap.js';

// Catch every fetch, not just apiRequest: several pages call fetch directly
// and their failures were invisible here.
installFetchTap();

/**
 * Staging-only network inspector.
 *
 * Mobile browsers have no network tab and Safari's Web Inspector needs a Mac on
 * the other end of a cable, so reviewing a staging change on a phone meant
 * guessing at what the app actually sent. This shows it.
 *
 * Renders nothing outside staging. Also serves as the environment banner: a
 * staging tab and a production tab are otherwise identical on a phone screen.
 */
/**
 * `tabs` lets the APP add panels without `shared/` importing from `apps/` --
 * the checklist knows about release checks and routes, which a layer meant to
 * be reusable by a future mobile or desktop target must not. Each entry is
 * `{ id, label, Component }`, and Component is rendered lazily, so a panel
 * nobody opens costs nothing.
 */
export default function DebugPanel({ tabs = [] }) {
  // Bailing before any hook keeps the component constant-foldable in production.
  if (!DEBUG_ENABLED) return null;
  return <DebugPanelInner tabs={tabs} />;
}

// A PR preview is staging config on a generated Pages hostname. OAuth cannot
// work there -- providers need exact pre-registered redirect URIs and these
// change per deployment -- so say so rather than letting it look like a bug.
//
// Two pages.dev hostnames are NOT previews: the bare project name is the
// production alias, and `main.` is the staging branch alias, which is the same
// deployment staging.oddsignals.ai serves and where OAuth does work.
const PAGES_PROJECT = 'oddisignals-app.pages.dev';
const NOT_PREVIEWS = [PAGES_PROJECT, `main.${PAGES_PROJECT}`];
const IS_PREVIEW =
  typeof window !== 'undefined' &&
  window.location.hostname.endsWith('.pages.dev') &&
  !NOT_PREVIEWS.includes(window.location.hostname);

// The point of the panel is getting a failure off the phone and into a chat,
// so the copied text has to stand on its own: what was called, what came back,
// and where. The backend returns the exception, message and failing line on
// staging, and that arrives inside `response` -- so copying the body verbatim
// is what makes a 500 actionable without CloudWatch.
function formatRequest(r) {
  return [
    `${r.method} ${r.url}`,
    `status: ${r.status || 'ERR'}${r.ok ? '' : '  (FAILED)'}`,
    `time:   ${r.ms}ms at ${r.at}`,
    `page:   ${typeof window !== 'undefined' ? window.location.href : ''}`,
    '',
    r.response,
  ].join('\n');
}

// navigator.clipboard needs a secure context and still refuses in some iOS
// Safari versions -- which is the exact device this panel exists for. Fall
// back to a hidden textarea plus execCommand, deprecated but still the only
// thing that works there.
async function toClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the textarea path */ }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    // Off-screen but still focusable; `display: none` would not be selectable.
    ta.style.cssText = 'position:fixed;top:-1000px;left:-1000px;opacity:0';
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);  // iOS ignores select() alone
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Silent success is indistinguishable from silent failure on a phone, where
// there is no console to check, so say which happened on the button itself.
async function copyWithFeedback(text, el) {
  const ok = await toClipboard(text);
  if (!el) return;
  const original = el.textContent;
  el.textContent = ok ? 'copied' : 'copy failed';
  setTimeout(() => { el.textContent = original; }, 1200);
}

function copyOne(r, el) {
  copyWithFeedback(formatRequest(r), el);
}

// Copies exactly what is on screen. When the list is filtered to failures
// that is the failures and nothing else, which is the whole point: pasting 50
// requests into a chat to report one 500 buries the one that matters.
function copyShown(items, el) {
  // Failures first: unfiltered, the interesting one is rarely on top.
  const sorted = [...items].sort((a, b) => Number(a.ok) - Number(b.ok));
  copyWithFeedback(sorted.map(formatRequest).join('\n\n----------------\n\n'), el);
}

// Which corner the badge sits in, remembered per browser. It has to be
// movable because it is fixed over the app: bottom-right covers the phone tab
// bar's "More", and bottom-left covers a sheet's cancel button, so wherever it
// sits by default it is in the way of something someone is trying to review.
const CORNER_KEY = 'os:debug-corner';
const CORNERS = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];

function readCorner() {
  try {
    const v = localStorage.getItem(CORNER_KEY);
    return CORNERS.includes(v) ? v : 'bottom-right';
  } catch (e) {
    return 'bottom-right';
  }
}

function cornerStyle(corner) {
  const [v, h] = corner.split('-');
  return {
    [v]: v === 'bottom' ? 'calc(12px + var(--safe-bottom))' : 'calc(12px + var(--safe-top))',
    [h]: 12,
  };
}

function DebugPanelInner({ tabs = [] }) {
  const [open, setOpen] = useState(false);
  const [corner, setCorner] = useState(readCorner);
  const dragRef = useRef(null);
  const [items, setItems] = useState(getRequests());
  const [expanded, setExpanded] = useState(null);
  const [failuresOnly, setFailuresOnly] = useState(false);
  // Which panel is showing. Network stays the default: the reason this thing
  // exists at all is seeing what the app sent.
  const [tab, setTab] = useState('network');

  useEffect(() => subscribe(() => setItems([...getRequests()])), []);

  const failing = items.filter((r) => !r.ok).length;
  // `!ok` rather than `status !== 200`: fetch counts 2xx as ok, so a 201 or a
  // 204 is a success, and a transport failure (DNS, CORS, timeout) has no
  // status at all -- those are recorded as status 0 and are exactly the ones
  // worth seeing.
  const shown = failuresOnly ? items.filter((r) => !r.ok) : items;

  return (
    <>
      {/* Drag to move, tap to open. One handler does both: a pointerup that
          travelled less than a few pixels is a tap, anything more is a drag —
          which is what lets a 44px target be draggable without swallowing the
          tap it also has to serve. Pointer events rather than touch events so
          the same code works with a mouse. */}
      <button
        aria-label="Staging debug panel (drag to move)"
        onPointerDown={(e) => {
          const el = e.currentTarget;
          el.setPointerCapture(e.pointerId);
          dragRef.current = { x: e.clientX, y: e.clientY, moved: false };
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          if (!d) return;
          if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > 6) d.moved = true;
        }}
        onPointerUp={(e) => {
          const d = dragRef.current;
          dragRef.current = null;
          if (!d) return;
          if (!d.moved) { setOpen((v) => !v); return; }
          // Snap to whichever corner the finger ended up nearest, rather than
          // leaving the badge floating: a fixed overlay at an arbitrary
          // offset ends up half off-screen on the next rotation.
          const next = `${e.clientY < window.innerHeight / 2 ? 'top' : 'bottom'}-${
            e.clientX < window.innerWidth / 2 ? 'left' : 'right'}`;
          setCorner(next);
          try { localStorage.setItem(CORNER_KEY, next); } catch (err) { /* private mode */ }
        }}
        style={{
          position: 'fixed', ...cornerStyle(corner), zIndex: 2147483000,
          background: failing ? '#A63D40' : '#B06B14', color: 'var(--color-surface)',
          border: 'none', borderRadius: 999, padding: '8px 14px',
          font: '600 12px/1 ui-monospace, SFMono-Regular, Menlo, monospace',
          letterSpacing: '.06em', boxShadow: '0 2px 10px rgba(0,0,0,.3)',
          touchAction: 'none',
        }}
      >
        {IS_PREVIEW ? 'PREVIEW' : 'STAGING'}{failing ? ` · ${failing}` : ''}
      </button>

      {open && (
        <div
          style={{
            position: 'fixed', zIndex: 2147482999,
            ...(corner.startsWith('top')
              ? { inset: '0 0 auto 0', borderTop: 'none',
                  borderBottom: '2px solid var(--color-warning)',
                  paddingTop: 'calc(10px + var(--safe-top) + 44px)' }
              : { inset: 'auto 0 0 0' }),
            maxHeight: '65dvh', overflowY: 'auto',
            background: 'var(--color-surface)', color: 'var(--color-line)',
            borderTop: '2px solid var(--color-warning)',
            font: '12px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace',
            padding: '10px 12px 72px',
          }}
        >
          {IS_PREVIEW && (
            <div style={{ background: 'var(--color-warning-tint)', border: '1px solid var(--color-warning)',
                          borderRadius: 4, padding: '8px 10px', marginBottom: 10,
                          color: 'var(--color-warning)', lineHeight: 1.45 }}>
              <strong>PR preview.</strong> Talks to the staging API through a
              same-origin proxy, so data is shared with staging and ordinary
              login works here. <strong>Connecting a social account does
              not</strong> — this hostname changes per deploy and cannot be
              registered with the providers. Check connect flows on
              staging.oddsignals.ai.
            </div>
          )}
          {/* Panel switcher. Only drawn when the app supplied one — with a
              single panel a tab strip is chrome that answers nothing. */}
          {tabs.length > 0 && (
            <div style={{ display: 'inline-flex', border: '1px solid var(--color-surface-overlay)',
                          borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
              {[{ id: 'network', label: 'NETWORK' }, ...tabs].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  aria-pressed={tab === t.id}
                  style={{
                    background: tab === t.id ? 'var(--color-surface-overlay)' : 'none',
                    color: tab === t.id ? 'var(--color-ink)' : 'var(--color-ink-subtle)',
                    border: 'none', padding: '4px 10px', font: 'inherit',
                    letterSpacing: '.08em', cursor: 'pointer',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {tabs.map((t) => (tab === t.id ? (
            <Suspense key={t.id} fallback={<div style={{ color: 'var(--color-ink-subtle)' }}>Loading…</div>}>
              <t.Component />
            </Suspense>
          ) : null))}

          <div style={{ display: tab === 'network' ? 'flex' : 'none', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <strong style={{ letterSpacing: '.08em' }}>NETWORK</strong>

            {/* Segmented, not a checkbox: which mode you are in has to be
                readable at a glance, because a filtered list that looks
                unfiltered reads as "the app stopped making requests". */}
            <span style={{ display: 'inline-flex', border: '1px solid var(--color-surface-overlay)',
                           borderRadius: 4, overflow: 'hidden' }}>
              {[['all', false], [`failures${failing ? ` (${failing})` : ''}`, true]].map(([label, val]) => (
                <button
                  key={String(val)}
                  onClick={() => setFailuresOnly(val)}
                  aria-pressed={failuresOnly === val}
                  style={{
                    background: failuresOnly === val ? 'var(--color-surface-overlay)' : 'none',
                    color: failuresOnly === val ? 'var(--color-ink)' : 'var(--color-ink-subtle)',
                    border: 'none', padding: '3px 8px', font: 'inherit', cursor: 'pointer',
                  }}
                >{label}</button>
              ))}
            </span>

            <span style={{ color: 'var(--color-ink-subtle)', marginRight: 'auto' }}>
              {shown.length} shown
            </span>
            <button
              onClick={(e) => copyShown(shown, e.currentTarget)}
              disabled={shown.length === 0}
              style={{ background: 'none', border: '1px solid var(--color-surface-overlay)',
                       color: shown.length ? 'var(--color-ink-muted)' : 'var(--color-ink-disabled)',
                       borderRadius: 4, padding: '3px 8px', font: 'inherit' }}
            >copy {shown.length === items.length ? 'all' : shown.length}</button>
            <button
              onClick={clearRequests}
              style={{ background: 'none', border: '1px solid var(--color-surface-overlay)',
                       color: 'var(--color-ink-muted)', borderRadius: 4, padding: '3px 8px', font: 'inherit' }}
            >clear</button>
          </div>

          {/* An empty filtered list and an empty buffer mean opposite things --
              "nothing is failing" versus "nothing has been called" -- so they
              must not share a message. */}
          {tab === 'network' && shown.length === 0 && (
            <div style={{ color: 'var(--color-ink-subtle)' }}>
              {items.length === 0
                ? 'No requests yet.'
                : `No failures in the last ${items.length} requests.`}
            </div>
          )}

          {tab === 'network' && shown.map((r) => (
            <div key={r.id} style={{ borderTop: '1px solid var(--color-surface-overlay)', padding: '6px 0' }}>
              <div
                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                style={{ display: 'flex', gap: 8, alignItems: 'baseline', cursor: 'pointer' }}
              >
                <span style={{ color: r.ok ? '#6FB088' : '#D97479', minWidth: 30 }}>
                  {r.status || 'ERR'}
                </span>
                <span style={{ color: 'var(--color-warning)', minWidth: 46 }}>{r.method}</span>
                <span style={{ flex: 1, wordBreak: 'break-all' }}>{r.url}</span>
                <span style={{ color: 'var(--color-ink-subtle)' }}>{r.ms}ms</span>
              </div>
              {expanded === r.id && (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); copyOne(r, e.currentTarget); }}
                    style={{ marginTop: 6, background: 'none', border: '1px solid var(--color-surface-overlay)',
                             color: 'var(--color-ink-muted)', borderRadius: 4, padding: '3px 8px', font: 'inherit' }}
                  >copy this request</button>
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                                color: 'var(--color-ink-muted)', margin: '6px 0 0', fontSize: 11 }}>
{r.response}
                  </pre>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
